import { useEffect } from "react";
import Modal from "../../../shared/Modal.tsx";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";
import TextInput from "../../../shared/form/fields/TextInput.tsx";
import RecommendationTargetSelect, {
  TargetOption,
} from "./RecommendationTargetSelect.tsx";
import {
  Activitystream,
  useActivitystreams,
} from "../../../../api/activitystreams.ts";
import { compact, find, forEach, map, omit, reduce, replace } from "lodash";
import FormActions from "../../../shared/form/fields/FormActions.tsx";
import { useMutation } from "react-query";
import { ApiError, fetchApi } from "../../../../helpers/reactQueryApi.ts";
import Checkbox from "../../../shared/form/fields/Checkbox.tsx";
import Hint from "../../../shared/form/fields/Hint.tsx";
import Select from "../../../shared/form/fields/Select.tsx";
import PleaseWaitAlert from "../../../shared/PleaseWaitAlert.tsx";
import { Activity } from "../../../../@types/activities.ts";
import formatDate from "../../../../helpers/formatDate.ts";
import { useUpdateActivityInCache } from "../../../../hooks/activitystreams/useUpdateActivityInCache.ts";

function senderStreamLabel(stream: Activitystream) {
  return stream.reply_to_email
    ? `${stream.name} <${stream.reply_to_email}>`
    : stream.name;
}

type FormData = {
  type: "internal" | "external";
  subject: string;
  activitystreams: TargetOption[];
  additional_message: string;
  include_replies?: boolean;
  include_subscribers?: boolean;
  include_attachments?: boolean;
  include_consumers?: boolean;
  external_recipients_list?: string;
  reply_to_activitystream_slug?: string;
};

type EmailRecommendationFormArgs = {
  activityId: string;
  type: "internal" | "external";
  selectableStreams: Activitystream[];
  close: () => void;
  emailRecommendationCount: { internal: number; external: number };
  emailRecommendationInfo: {
    hasAttachments: boolean;
    totalAttachmentSize: number;
    lastRecommendation?: Activity["email_recommendation"];
  };
  teaser?: string;
};

export default function EmailRecommendationForm({
  type,
  close,
  selectableStreams,
  activityId,
  emailRecommendationInfo,
  emailRecommendationCount,
  teaser,
}: EmailRecommendationFormArgs) {
  const { hasAttachments, totalAttachmentSize, lastRecommendation } =
    emailRecommendationInfo;
  const attachmentSizeLimit =
    Preload.current_network.config.email_recommendations.attachment_size_limit;
  const attachmentsInLimit = totalAttachmentSize <= attachmentSizeLimit;
  const maxExternalRecipients =
    Preload.current_network.config.email_recommendations
      .max_external_recipients;

  const { updateActivity } = useUpdateActivityInCache();

  const methods = useForm<FormData>({
    defaultValues: {
      type: type || "internal",
      activitystreams:
        type === "internal"
          ? map(selectableStreams, (stream) => ({
              type: "stream" as const,
              label: stream.name,
              value: stream.slug,
            }))
          : undefined,
      subject: `${I18n.t(
        "js.activitystreams.email_recommendations.default_subject",
      )} - ${teaser ? replace(teaser, /\n/g, " ") : ""}`,
      additional_message: "",
      include_attachments: hasAttachments && attachmentsInLimit,
      include_replies:
        type === "internal"
          ? Preload.current_network.config.email_recommendations
              .include_replies_default
          : undefined,
    },
  });
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const { mutate: recommend, isLoading } = useMutation(
    (
      body: Omit<FormData, "activitystreams" | "external_recipients_list"> & {
        activitystream_slugs?: string[];
        membership_ids?: string[];
        external_recipients?: string[] | null;
      },
    ) =>
      fetchApi(`/activities/${activityId}/email_recommendations`, {
        method: "POST",
        body,
      }),
    {
      onSuccess: () => {
        close();
        toastr.success(
          I18n.t("js.activitystreams.email_recommendations.success"),
        );
        updateActivity(activityId, (activity) => ({
          ...activity,
          [`${type}_email_recommendation_count`]:
            activity[`${type}_email_recommendation_count`] + 1,
        }));
      },
      onError: (error) => {
        const responseMessage =
          error instanceof ApiError ? error.responseJson?.message : undefined;
        const serverMessages = Array.isArray(responseMessage)
          ? (responseMessage as string[])
          : [];

        if (
          serverMessages.includes(
            "activitystreams.email_recommend_activity.too_many_external_recipients",
          )
        ) {
          toastr.error(
            I18n.t(
              "js.activitystreams.email_recommendations.too_many_recipients",
              { max: maxExternalRecipients },
            ),
          );
        } else if (
          serverMessages.includes(
            "activitystreams.email_recommend_activity.no_valid_recipients",
          )
        ) {
          toastr.error(
            I18n.t(
              "js.activitystreams.email_recommendations.no_valid_recipients",
            ),
          );
        } else if (
          serverMessages.includes(
            "activitystreams.email_recommend_activity.attachment_size_limit_exceeded",
          )
        ) {
          toastr.error(
            I18n.t(
              "js.activitystreams.email_recommendations.attachments_too_large",
              { limit: `${attachmentSizeLimit / 1024 / 1024}MB` },
            ),
          );
        } else if (error instanceof ApiError && error.status === 403) {
          // The context menu shows the entry as soon as *one* visible stream
          // allows it, while the policy requires *every* stream of the activity
          // — including crosspost targets we cannot see from here, so the menu
          // cannot replicate the decision. Name the reason instead of the
          // generic error.
          toastr.error(
            I18n.t("js.activitystreams.email_recommendations.not_allowed"),
          );
        } else {
          toastr.error(I18n.t("js.generic_error"));
        }
      },
    },
  );

  const selectedTargets: TargetOption[] = watch("activitystreams") || [];
  const selectedStreamValues = selectedTargets.filter(
    (t) => t.type === "stream" || !t.type,
  );
  const selectedStreams = compact(
    map(selectedStreamValues, ({ value }) =>
      find(selectableStreams, { slug: value }),
    ),
  );

  // Selected groups with sender settings, the first one applies by default (tixxt/core#534).
  // Looked up among all streams: selectableStreams only holds the activity's own.
  const { data: allStreams } = useActivitystreams();
  const senderStreams = compact(
    map(selectedStreamValues, ({ value }) => find(allStreams, { slug: value })),
  ).filter((stream) => stream.sender_name || stream.reply_to_email);
  const chosenSlug = watch("reply_to_activitystream_slug");
  const senderStream =
    find(senderStreams, { slug: chosenSlug }) || senderStreams[0];

  // a deselected group must not stay chosen
  useEffect(() => {
    if (chosenSlug && !find(senderStreams, { slug: chosenSlug })) {
      setValue("reply_to_activitystream_slug", "");
    }
  }, [chosenSlug, senderStreams, setValue]);

  const totalSubscriberCount = reduce(
    selectedStreams,
    (sum, stream) => sum + (stream.subscriber_count || 0),
    0,
  );

  function submit(data: FormData) {
    const streamSlugs =
      type === "internal"
        ? data.activitystreams
            ?.filter((t) => t.type === "stream" || !t.type)
            .map((t) => t.value) || []
        : [];
    const memberIds =
      type === "internal"
        ? data.activitystreams
            ?.filter((t) => t.type === "member")
            .map((t) => t.value) || []
        : [];

    const externalRecipients =
      type === "external"
        ? data.external_recipients_list?.match(
            /([a-zA-Z0-9._+-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/g,
          )
        : null;

    // The regex above silently drops anything that is not an email address —
    // without this guard the request goes out with an empty recipient list and
    // the server answers 201 for a mail nobody receives.
    if (type === "external" && !externalRecipients?.length) {
      toastr.error(
        I18n.t("js.activitystreams.email_recommendations.no_valid_recipients"),
      );
      return;
    }

    if (
      externalRecipients &&
      maxExternalRecipients > 0 &&
      externalRecipients.length > maxExternalRecipients
    ) {
      toastr.error(
        I18n.t("js.activitystreams.email_recommendations.too_many_recipients", {
          max: maxExternalRecipients,
        }),
      );
      return;
    }

    const externalRecipientsList =
      type === "external" ? { external_recipients: externalRecipients } : {};

    recommend({
      ...omit(data, ["activitystreams", "external_recipients_list"]),
      ...(streamSlugs.length > 0 ? { activitystream_slugs: streamSlugs } : {}),
      ...(memberIds.length > 0 ? { membership_ids: memberIds } : {}),
      ...externalRecipientsList,
    });
  }

  function onValidationError(errors: FieldErrors<FormData>) {
    forEach(errors, (error) => {
      toastr.error(error?.message || I18n.t("generic_error"));
    });
  }

  const attachmentsCheckbox = hasAttachments && (
    <div>
      <Checkbox
        name="include_attachments"
        label={I18n.t(
          "js.activitystreams.email_recommendations.include_attachments_label",
        )}
        disabled={!attachmentsInLimit}
      />
      {!attachmentsInLimit && (
        <Hint
          text={I18n.t(
            "js.activitystreams.email_recommendations.attachments_too_large",
            { limit: `${attachmentSizeLimit / 1024 / 1024}MB` },
          )}
        />
      )}
    </div>
  );

  return (
    <Modal
      title={I18n.t(`js.activitystreams.email_recommend_activity_${type}ly`)}
      onClose={close}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submit, onValidationError)}>
          <div className="flex flex-col gap-2 modal-body p-0 mb-4">
            <span className="help-block">
              {I18n.t("js.activitystreams.email_recommendations.disclaimer")}
            </span>
            {emailRecommendationCount && emailRecommendationCount[type] > 0 && (
              <div className="alert alert-info">
                {I18n.t(
                  `js.activitystreams.email_recommendations.${
                    lastRecommendation?.membership_id ===
                    Preload.current_member?.id
                      ? "last_own_recommendation"
                      : "last_recommendation"
                  }`,
                  {
                    membership_name: lastRecommendation?.membership_name,
                    last_recommended_at:
                      lastRecommendation &&
                      formatDate(
                        new Date(lastRecommendation?.created_at),
                        "PPPP HH:mm",
                      ),
                  },
                )}
              </div>
            )}
            <TextInput
              name="subject"
              label={I18n.t(
                "js.activitystreams.email_recommendations.subject_label",
              )}
            />
            <TextInput
              name="additional_message"
              label={I18n.t(
                "js.activitystreams.email_recommendations.additional_message_label",
              )}
              placeholder={I18n.t(
                "js.activitystreams.email_recommendations.additional_message_placeholder",
              )}
              multiline
            />
            {type === "external" ? (
              <>
                <TextInput
                  name="external_recipients_list"
                  label={I18n.t(
                    "js.activitystreams.email_recommendations.external_recipients_list_label",
                  )}
                  required={I18n.t("activity.validation.select_stream")}
                  error={!!errors.external_recipients_list}
                  multiline
                  rows={8}
                  hint={compact([
                    I18n.t(
                      "js.activitystreams.email_recommendations.external_email_hint",
                    ),
                    maxExternalRecipients > 0 &&
                      I18n.t(
                        "js.activitystreams.email_recommendations.max_external_recipients_hint",
                        { max: maxExternalRecipients },
                      ),
                  ]).join(" ")}
                />
                {Preload.current_network.config.email_recommendations
                  .reply_to_actor_on_external && (
                  <Hint
                    text={I18n.t(
                      "js.activitystreams.email_recommendations.external_email_reply_to_only_hint",
                    )}
                  />
                )}
                {attachmentsCheckbox}
              </>
            ) : (
              <>
                <RecommendationTargetSelect
                  filterCan="email_recommend_activity_internally"
                  hint={I18n.t(
                    "js.activitystreams.email_recommendations.visibility_hint",
                  )}
                />
                {senderStreams.length > 1 && (
                  <Select
                    name="reply_to_activitystream_slug"
                    label={I18n.t(
                      "js.activitystreams.email_recommendations.reply_to_group_label",
                    )}
                    options={senderStreams.map((stream) => ({
                      value: stream.slug,
                      label: senderStreamLabel(stream),
                    }))}
                    placeholder={senderStreamLabel(senderStreams[0])}
                    isSearchable={false}
                    stringValues
                  />
                )}
                {senderStream?.reply_to_email && (
                  <Hint
                    text={I18n.t(
                      "js.activitystreams.email_recommendations.reply_to_group_hint",
                      { email: senderStream.reply_to_email },
                    )}
                  />
                )}
                {Preload.current_network.config.email_recommendations
                  .reply_to_actor_on_internal &&
                  !senderStream?.reply_to_email && (
                    <span className="help-block">
                      {I18n.t(
                        "js.activitystreams.email_recommendations.reply_to_hint",
                      )}
                      <ul className="list-disc list-inside">
                        {Preload.current_network.config.email_recommendations
                          .reply_to_actor_on_internal && (
                          <li>
                            {I18n.t(
                              "js.activitystreams.email_recommendations.group_members",
                            )}
                          </li>
                        )}
                        {Preload.current_network.config.email_recommendations
                          .reply_to_actor_on_internal &&
                          Preload.current_network.config.consumers_enabled && (
                            <li>
                              {I18n.t(
                                "js.activitystreams.email_recommendations.consumers",
                              )}
                            </li>
                          )}
                      </ul>
                    </span>
                  )}
                {Preload.current_network.config.consumers_enabled && (
                  <Checkbox
                    name="include_consumers"
                    label={I18n.t(
                      "js.activitystreams.email_recommendations.include_consumers_label",
                    )}
                  />
                )}
                <div className="block">
                  <Checkbox
                    name="include_replies"
                    label={I18n.t(
                      "js.activitystreams.email_recommendations.include_replies_label",
                    )}
                  />
                  {totalSubscriberCount > 0 && (
                    <Checkbox
                      name="include_subscribers"
                      label={I18n.t(
                        "js.activitystreams.email_recommendations.include_subscribers_label",
                        { subscribers_count: totalSubscriberCount },
                      )}
                    />
                  )}
                  {attachmentsCheckbox}
                </div>
              </>
            )}
            {isLoading && <PleaseWaitAlert />}
          </div>
          <div className="modal-footer">
            <FormActions
              onCancel={close}
              saveText={I18n.t(
                "js.activitystreams.email_recommendations.submit",
              )}
              saveDisabled={isLoading}
            />
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}
